import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import LimeLogo from "../../assets/lime-logo-no-bg.png";

// Category labels are 12px text on the solarpunk homepage. Six saturated hues
// (purple/blue/pink/cyan) were exactly the incidental accents the palette fold
// removed everywhere else — they just survived here because this component sits
// in components/home/ rather than in the page file the redesign rewrote.
//
// Differentiation is now tonal within the palette (moss shades + the earthy amber
// already used for the featured star) rather than by inventing hues. A tiny
// category label does not need six colours to be legible; the card is already
// distinguished by its name, icon and tagline.
const CATEGORY_COLORS = {
  ai:              "text-moss-light",
  saas:            "text-silver",
  "developer-tool":"text-moss",
  productivity:    "text-amber-400",
  ecommerce:       "text-amber-200",
  mobile:          "text-silver-medium",
  other:           "text-silver-dark",
};

function AppIcon({ iconUrl, name }) {
  if (iconUrl) {
    return <img src={iconUrl} alt={name} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />;
  }
  return (
    <div className="w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center text-silver-dark font-bold text-sm flex-shrink-0">
      {name?.[0]?.toUpperCase() || "?"}
    </div>
  );
}

function AppCard({ listing }) {
  const color = CATEGORY_COLORS[listing.category] || CATEGORY_COLORS.other;
  const isAndroid = listing.platform === "android" || listing.platform === "cross-platform";

  return (
    <div className="flex-shrink-0 w-64 bg-black border border-gray-800 rounded-xl p-4 hover:border-gray-600 transition-colors">
      <div className="flex items-start gap-3 mb-3">
        <AppIcon iconUrl={listing.iconUrl} name={listing.name} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-medium text-silver-light text-sm truncate">{listing.name}</p>
            {listing.featured && <span className="text-xs text-amber-300 flex-shrink-0">★</span>}
          </div>
          <p className={`text-xs ${color}`}>{listing.category?.replace("-", " ")}</p>
        </div>
      </div>
      <p className="text-silver-dark text-xs leading-relaxed line-clamp-2 mb-4">{listing.tagline}</p>
      <div className="flex gap-2">
        {listing.appUrl && (
          <a
            href={listing.appUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-center text-xs bg-gray-900 hover:bg-gray-800 border border-gray-700 text-silver-medium rounded-lg py-1.5 transition-colors"
          >
            Visit →
          </a>
        )}
        {isAndroid && listing.apkUrl && (
          <a
            href={listing.apkUrl}
            className="flex-1 text-center text-xs bg-lime-900/20 border border-lime-800/40 text-lime-400 rounded-lg py-1.5 transition-colors hover:bg-lime-900/40"
          >
            APK ↓
          </a>
        )}
      </div>
    </div>
  );
}

export default function MarketplaceSection() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get("/api/marketplace/listings/recent?limit=8")
      .then(r => setListings(r.data.listings || []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, []);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 280, behavior: "smooth" });
  };

  // Don't render if still loading and empty — avoid layout shift
  if (!loading && listings.length === 0) return null;

  return (
    <section className="py-20 bg-black border-t border-gray-900">
      <div className="container mx-auto px-4">

        {/* Header */}
        <div className="flex items-end justify-between mb-8 max-w-6xl mx-auto">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <img src={LimeLogo} alt="" className="w-5 h-5" />
              <span className="text-xs uppercase tracking-widest text-lime-500">Limelite Marketplace</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-silver-light">
              Cool new apps
            </h2>
            <p className="text-silver-dark text-sm mt-1">Built by indie devs and teams — deployed on Limelite</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => scroll(-1)} className="w-8 h-8 rounded-full border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light flex items-center justify-center transition-colors text-lg">‹</button>
            <button onClick={() => scroll(1)}  className="w-8 h-8 rounded-full border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light flex items-center justify-center transition-colors text-lg">›</button>
            <Link to="/limelite/marketplace" className="hidden sm:block text-sm text-lime-600 hover:text-lime-400 transition-colors ml-2">
              Browse all →
            </Link>
          </div>
        </div>

        {/* Carousel */}
        {loading ? (
          <div className="flex gap-4 max-w-6xl mx-auto overflow-hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-64 h-36 bg-off-black border border-gray-800 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 max-w-6xl mx-auto"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {listings.map(l => <AppCard key={l._id} listing={l} />)}

            {/* "List yours" card at the end */}
            <div className="flex-shrink-0 w-64 border border-dashed border-gray-700 rounded-xl p-4 flex flex-col items-center justify-center text-center hover:border-lime-700 transition-colors group">
              <img src={LimeLogo} alt="" className="w-8 h-8 mb-3 opacity-40 group-hover:opacity-70 transition-opacity" />
              <p className="text-silver-dark text-sm mb-3">Ship something and get it listed here</p>
              <Link
                to="/limelite"
                className="text-xs text-lime-600 hover:text-lime-400 transition-colors border border-lime-800/40 rounded px-3 py-1.5"
              >
                Deploy now →
              </Link>
            </div>
          </div>
        )}

        <div className="text-center mt-8">
          <Link to="/limelite/marketplace" className="text-sm text-silver-dark hover:text-silver-medium transition-colors underline underline-offset-2">
            Browse all apps on the marketplace →
          </Link>
        </div>
      </div>
    </section>
  );
}
